import { useCallback, useEffect, useState } from "react";

export interface ColumnWidthsApi {
  getWidth: (colKey: string) => number | undefined;
  setWidth: (colKey: string, width: number) => void;
  resetWidth: (colKey: string) => void;
  resetAll: () => void;
}

const DEFAULT_MIN_WIDTH = 60;

/**
 * Persiste el ancho de las columnas de una tabla en localStorage.
 * Misma clave patrón que useColumnVisibility ("table-column-widths-${tableId}").
 * Sólo se guardan anchos explícitos (no defaults), así un cambio en defaultWidth
 * se refleja para usuarios nuevos sin perder overrides individuales.
 */
export function useColumnWidths(tableId?: string): ColumnWidthsApi {
  const storageKey = tableId ? `table-column-widths-${tableId}` : null;

  const [widths, setWidths] = useState<Record<string, number>>(() => {
    if (typeof window === "undefined" || !storageKey) return {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
      return {};
    }
  });

  // Re-leer cuando cambia tableId (cada tabla tiene su propio espacio).
  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) {
      setWidths({});
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      setWidths(raw ? (JSON.parse(raw) as Record<string, number>) : {});
    } catch {
      setWidths({});
    }
  }, [storageKey]);

  const setWidth = useCallback(
    (colKey: string, width: number) => {
      setWidths((prev) => {
        const next = { ...prev, [colKey]: width };
        if (storageKey && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(storageKey, JSON.stringify(next));
          } catch {
            // Sin espacio / deshabilitado: ignorar silenciosamente.
          }
        }
        return next;
      });
    },
    [storageKey],
  );

  const resetAll = useCallback(() => {
    setWidths({});
    if (storageKey && typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  }, [storageKey]);

  const resetWidth = useCallback(
    (colKey: string) => {
      setWidths((prev) => {
        if (!(colKey in prev)) return prev;
        const next = { ...prev };
        delete next[colKey];
        if (storageKey && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(storageKey, JSON.stringify(next));
          } catch {
            // ignore
          }
        }
        return next;
      });
    },
    [storageKey],
  );

  const getWidth = useCallback(
    (colKey: string) => {
      const w = widths[colKey];
      if (typeof w !== "number") return undefined;
      return Math.max(w, DEFAULT_MIN_WIDTH);
    },
    [widths],
  );

  return { getWidth, setWidth, resetWidth, resetAll };
}

export const COLUMN_RESIZE_MIN_WIDTH = DEFAULT_MIN_WIDTH;
